import { useCallback, useEffect, useRef, useState } from 'react';

import type { RoleplayScenario, RoleplaySession } from 'services/api/roleplayClient';
import { useRoleplaySession } from 'pages/RoleplayPractice/useRoleplaySession';
import { useRoleplayVoiceSession } from 'pages/RoleplayPractice/useRoleplayVoiceSession';
import {
  roleplayHasConfirmedCompletion, roleplayWantsReflection, voiceRoleplayPresentationInstructions,
} from './voiceRoleplayPresentation';
import type { VoiceRoleplayPresentation, VoiceRoleplaySettings } from './voiceRoleplayPresentation';

export type VoiceRoleplayPhase = 'inactive' | 'prebrief' | 'practice' | 'paused' | 'recovery'
  | 'end_confirmation' | 'control_clarification' | 'clarify' | 'support' | 'reflection'
  | 'reflection_confirmation' | 'feedback_loading' | 'feedback_recovery' | 'feedback';

export interface VoiceRoleplayHostParams {
  flowName: string;
  flowRunId: number | null;
  questionId: number | null;
  draft?: VoiceRoleplayDraft | null;
  /** User/module presentation scope; it grants no actor or completion authority. */
  conversationScope?: string;
  integrated?: boolean;
  voiceSettings?: VoiceRoleplaySettings;
  guide: {
    speak: (text: string) => void | Promise<void>;
    release: () => Promise<void>;
    reconnect: () => void | Promise<void>;
    /** Cancel only the host's current guide utterance; never grants delivery. */
    interrupt?: () => void;
    /** Existing validated guide controls only; never actor/evaluator actions. */
    command?: (command: 'pause_capture' | 'resume_capture' | 'settings') => Promise<boolean>;
    /** Resolves true only for full owned audible playback, never a completion receipt. */
    converse?: (instructions: string) => Promise<boolean>;
  };
  onContinue: (session: RoleplaySession) => void | Promise<void>;
  onLeave: () => void | Promise<void>;
  /** Delegates the existing roleplay crisis lock to the parent safety surface. */
  onCrisis?: (session: RoleplaySession) => void | Promise<void>;
}

export interface VoiceRoleplayDraft {
  phase: 'reflection' | 'reflection_confirmation';
  text: string;
  roleplay_session_id: number;
}

export interface VoiceRoleplayControlTranscript {
  id: string;
  text: string;
  timestamp: string;
  conversationScope: string;
  flowRunId: number;
  questionId: number;
  roleplaySessionId: number;
}

const normalize = (text: string) => text.toLowerCase().replace(/[.,!?;:]/g, '').replace(/\s+/g, ' ').trim();
const normalizeGuideIntent = (transcript: string, integrated: boolean) => {
  if (!integrated) return normalize(transcript);
  return normalize(transcript.trim().replace(/^(?:wise\s+friend|wisefriend)(?=[\s.,!?;:]|$)[\s.,!?;:]*/i, ''));
};
const isYes = (text: string) => /^(yes|yes please|confirm|that's right|that is right|correct)$/.test(text);
const isNo = (text: string) => /^(no|no thanks|cancel|cancel that)$/.test(text);
const isDefer = (text: string) => /^(?:defer|skip(?: this)?(?: question| practice)?(?: for now)?|come back(?: to this)?(?: later)?|answer(?: this)? later)$/.test(text);
const isGuideControl = (text: string) => /^(?:settings|voice settings|change voice|read settings|pause(?: capture)?|resume(?: capture)?)$/.test(text);
const legacyReadiness = /^(?:begin(?: practice)?|start(?: practice)?|ready|i'm ready|i am ready)$/;
const practiceTarget = '(?:(?:(?:the|this|my|our) )?(?:roleplay|practice|scenario|scene|activity|exercise|session)|it|this)';
// Match an entire explicit affirmative request, never a readiness fragment
// inside negation, uncertainty, a quotation or substantive scene dialogue.
const integratedReadiness = new RegExp([
  '^(?:(?:yes|yeah|yep|ok(?:ay)?|sure|all right|alright) )?(?:please )?',
  '(?:',
  `(?:(?:i['’]?m|i am) )?ready(?: (?:to (?:begin|start)(?: ${practiceTarget})?|for ${practiceTarget}))?`,
  '|',
  `(?:(?:let['’]?s|let us|(?:can|could|would|will) (?:you|we|i)|i (?:want|would like) to) )?(?:begin|start)(?: ${practiceTarget})?`,
  ')(?: (?:right )?now)?(?: please)?$',
].join(''));
const isPracticeReadiness = (text: string, integrated: boolean) => legacyReadiness.test(text)
  || (integrated && (integratedReadiness.test(text)
    || /^(?:yes(?: please)?|sure|ok(?:ay)?|sounds good|go ahead|let'?s do it)$/.test(text)));
const isPracticeReadinessOrResume = (text: string, integrated: boolean) => isPracticeReadiness(text, integrated)
  || /^(?:resume practice|continue practice)$/.test(text);
const GUIDE_HELP_PHASES: VoiceRoleplayPhase[] = ['prebrief', 'reflection', 'reflection_confirmation', 'feedback', 'feedback_recovery'];
const isGuideHelp = (text: string) => {
  const request = text.replace(/^(?:sorry\s+)?(?:(?:please\s+)?(?:can|could|would|will) you\s+)?(?:please\s+)?/, '')
    .replace(/(?:\s+(?:please|for me)){1,2}$/, '');
  return /^(?:rephrase(?: (?:the question|that|it))?|simplify the question|ask (?:me )?(?:the question|that|it) (?:another|a different) way|(?:say|put) (?:that|it) (?:differently|another way|(?:in )?a different way)|make the question simpler|explain(?: (?:the question|that|it|what you mean))?|clarify the question|help me understand the question)$/.test(request)
    || /^(?:what do you mean|what does that mean|what are you asking me|i don't understand(?: the question| that)|i do not understand the question)$/.test(text)
    || /^(?:repeat (?:(?:the|this|that|your) question|that)(?: again)?|(?:ask (?:me )?(?:(?:the|this|that) question|that)|(?:say|read) (?:the|this|that) question|say that) again)$/.test(request)
    || /^(?:what was the question|what is the question)$/.test(text)
    || /^(?:(?:give|show|provide)(?: me)? )?(?:(?:(?:some|more|different|other|a few|a couple of) )?examples|(?:(?:an|another|one|one more|a different) )?example)(?: (?:of|for) (?:this|that|the question))?$/.test(request);
};
const hasFeedback = (session: RoleplaySession) => Boolean(
  session.debrief?.summary || session.debrief?.strengths?.length
  || session.debrief?.next_steps?.length || session.debrief?.evidence?.length,
);
export const voiceRoleplayPrebrief = (scenario: RoleplayScenario) => [
  `Practice with ${scenario.persona.name}. ${scenario.title}.`,
  scenario.subtitle,
  scenario.situation,
  `Your role: ${scenario.learner_role}.`,
  `What to practice: ${scenario.learning_objectives.join('. ')}.`,
  'Say begin practice when you are ready. During the practice, say Wise Friend, end practice to end, or Wise Friend, pause practice to pause.',
].filter(Boolean).join(' ');

export const voiceRoleplayFeedback = (session: RoleplaySession) => {
  const won = session.status === 'completed'
    && session.completion_reason === 'win_condition_met';
  return [
    won ? 'Goal met.' : 'Goal not met yet.',
    session.debrief?.summary,
    session.debrief?.strengths?.length ? `What worked: ${session.debrief.strengths.join('. ')}.` : '',
    session.debrief?.next_steps?.length ? `Try next time: ${session.debrief.next_steps.join('. ')}.` : '',
    session.win_evaluation?.rationale,
    session.win_evaluation?.evidence?.length ? `Evidence: ${session.win_evaluation.evidence.join('. ')}.` : '',
    won ? 'Say continue to return to the module, or practice again.' : 'Say practice again, or leave practice.',
  ].filter(Boolean).join(' ');
};

/** Owns guide → actor → guide microphone transfer for one pinned flow item. */
export const useVoiceRoleplayHost = (params: VoiceRoleplayHostParams) => {
  const enabled = params.questionId !== null && params.flowRunId !== null;
  const integrated = Boolean(params.integrated && params.guide.converse);
  const controller = useRoleplaySession(
    params.flowName, params.flowRunId ?? 0, params.questionId, enabled,
  );
  const [phase, setPhase] = useState<VoiceRoleplayPhase>('inactive');
  const [error, setError] = useState<string | null>(null);
  const [reflection, setReflection] = useState('');
  const [controlTranscriptState, setControlTranscriptState] = useState<{
    hostKey: string; transcripts: VoiceRoleplayControlTranscript[];
  }>({ hostKey: '', transcripts: [] });
  const controlTranscriptSequence = useRef(0);
  const latest = useRef({ params, controller, phase });
  latest.current = { params, controller, phase };
  const actorRef = useRef<ReturnType<typeof useRoleplayVoiceSession> | null>(null);
  const hostGeneration = useRef(0);
  const busy = useRef(false);
  const queuedGuideTranscript = useRef('');
  const queuedGuideClarification = useRef(false);
  const guideTranscriptHandler = useRef<(transcript: string) => Promise<boolean>>(async () => true);
  const delivered = useRef('');
  const guideOwnsMicrophone = useRef(true);
  const guideReturn = useRef<{ generation: number; promise: Promise<void> } | null>(null);
  const actorHandoff = useRef<{
    generation: number; intent: number; sessionId: number; hostKey: string;
    phase: VoiceRoleplayPhase; cancelled: boolean; promise: Promise<void>;
  } | null>(null);
  const releasedGuideHandoff = useRef<{ generation: number; promise: Promise<void> } | null>(null);
  const automaticActorHandoff = useRef('');
  const actorRetryCount = useRef(0);
  const suspendedPhase = useRef<VoiceRoleplayPhase>('practice');
  const intentRevision = useRef(0);
  const feedbackPreparation = useRef('');
  const feedbackAutoHeld = useRef(false);
  const feedbackPlayback = useRef<{ generation: number; intent: number; sessionId: number } | null>(null);
  const continued = useRef('');
  const hostKey = enabled ? `${params.conversationScope ?? ''}:${params.flowName}:${params.flowRunId}:${params.questionId}` : '';
  const isEnabled = useCallback(() => latest.current.params.questionId !== null
    && latest.current.params.flowRunId !== null, []);
  const ownsActorHost = useCallback(() => isEnabled() && hostKey
    === `${latest.current.params.conversationScope ?? ''}:${latest.current.params.flowName}:${latest.current.params.flowRunId}:${latest.current.params.questionId}`,
  [hostKey, isEnabled]);

  const speak = useCallback(async (text: string) => {
    if (!isEnabled()) return;
    await latest.current.params.guide.speak(text);
  }, [isEnabled]);

  const present = useCallback(async (kind: VoiceRoleplayPresentation, session?: RoleplaySession | null, helpRequest?: string) => {
    const current = latest.current;
    if (!isEnabled() || !current.params.integrated || !current.params.guide.converse || !current.controller.scenario) return false;
    const instructions = voiceRoleplayPresentationInstructions(
      kind, current.controller.scenario, current.params.voiceSettings, session ?? current.controller.session, reflection,
    );
    return current.params.guide.converse(instructions + (helpRequest
      ? `\nThe learner requested help: ${JSON.stringify(helpRequest)}. Rephrase or explain only the current public task naturally as requested; preserve phase, facts and progress. Do not restart or complete an activity. Authored examples are preferred; otherwise clearly hypothetical examples may illustrate the public task without inventing an assessment or scene event. Do not repeat a menu.` : ''));
  }, [isEnabled, reflection]);

  const continueAfterFeedback = useCallback(async () => {
    const current = latest.current;
    const playback = feedbackPlayback.current;
    const session = current.controller.session;
    if (!current.params.integrated || !current.params.guide.converse || !playback || !session
      || !isEnabled() || current.phase !== 'feedback' || busy.current
      || playback.generation !== hostGeneration.current || playback.intent !== intentRevision.current
      || playback.sessionId !== session.id || !roleplayHasConfirmedCompletion(session)
      || session.ui_mode === 'crisis' || session.ui_mode === 'clarify'
      || session.ai_safety?.status === 'pending' || session.actor_error
      || current.controller.processing || current.controller.terminalTransitionPending) return;
    const key = `${hostGeneration.current}:${session.id}`;
    if (continued.current === key) return;
    continued.current = key;
    try {
      await current.params.onContinue(session);
    } catch (failure) {
      if (continued.current === key && playback.generation === hostGeneration.current) {
        continued.current = '';
        // A failed advance is explicitly retryable after another heard feedback;
        // it must not loop automatically from the same delivery evidence.
        feedbackPlayback.current = null;
      }
      throw failure;
    }
  }, [isEnabled]);

  const deliverFeedback = useCallback(async (detail = false) => {
    const generation = hostGeneration.current;
    const intent = intentRevision.current;
    const session = latest.current.controller.session;
    feedbackAutoHeld.current = false;
    feedbackPlayback.current = null;
    const heard = await present(detail ? 'feedback_detail' : 'feedback', session);
    if (!heard || generation !== hostGeneration.current || intent !== intentRevision.current
      || latest.current.controller.session?.id !== session?.id || !isEnabled()) return;
    feedbackPlayback.current = { generation, intent, sessionId: session!.id };
    await continueAfterFeedback();
  }, [continueAfterFeedback, isEnabled, present]);

  const returnToGuide = useCallback(async () => {
    if (!isEnabled()) return;
    const generation = hostGeneration.current;
    const releasing = releasedGuideHandoff.current;
    if (releasing?.generation === generation) {
      // A terminal/safety update can arrive while a handoff is releasing the
      // guide. Let that transfer restore its safe connection before queuing
      // new guide speech, which an explicit reconnect would otherwise retire.
      await releasing.promise;
      if (generation !== hostGeneration.current || !isEnabled()) return;
    }
    // Actor controls and terminal/safety updates can return concurrently. A
    // guide-owned flag does not mean the pending reconnection has finished.
    const pending = guideReturn.current;
    if (pending?.generation === generation) return pending.promise;
    if (guideOwnsMicrophone.current) return;
    const transfer = { generation, promise: Promise.resolve() };
    transfer.promise = (async () => {
      await actorRef.current?.disconnectAsync();
      if (generation !== hostGeneration.current || !isEnabled()
        || latest.current.controller.session?.status === 'crisis_paused'
        || latest.current.controller.session?.ui_mode === 'crisis' || latest.current.phase === 'support') return;
      guideOwnsMicrophone.current = true;
      await latest.current.params.guide.reconnect();
    })();
    guideReturn.current = transfer;
    try { await transfer.promise; }
    finally { if (guideReturn.current === transfer) guideReturn.current = null; }
  }, [isEnabled]);

  const restoreReleasedGuide = useCallback(async (generation: number) => {
    const current = latest.current;
    if (generation !== hostGeneration.current || !isEnabled()
      || current.controller.session?.status === 'crisis_paused'
      || current.controller.session?.ui_mode === 'crisis' || current.phase === 'support') return;
    // release() has already closed the guide even though actor ownership was
    // never granted. Restore the current guide explicitly; returnToGuide's
    // already-owned shortcut cannot recover this interrupted transfer.
    guideOwnsMicrophone.current = true;
    await current.params.guide.reconnect();
  }, [isEnabled]);

  const canHandToActor = useCallback((sessionId?: number) => {
    const current = latest.current;
    const session = current.controller.session;
    const secondsLeft = Number.isFinite(current.controller.secondsLeft)
      ? current.controller.secondsLeft : session?.seconds_left;
    const clockCanExpire = !session?.learner_clock || session.learner_clock.running_since !== null;
    return Boolean(isEnabled() && session && (sessionId === undefined || session.id === sessionId)
      && session.flow_run_id === current.params.flowRunId && session.scenario.question_id === current.params.questionId
      && ['active', 'closing'].includes(session.status) && current.controller.screen === 'practice'
      && !current.controller.loading && !current.controller.processing && !current.controller.terminalTransitionPending
      && session.ui_mode !== 'crisis' && session.ui_mode !== 'clarify'
      && session.ai_safety?.status !== 'pending' && !session.actor_error
      // Match the controller timer: a negotiated paused clock resumes through
      // its canonical claim/ready handshake. Expired legacy/running attempts
      // end first, while an already-authorized actor directive may finish.
      && !(session.status === 'active' && clockCanExpire
        && typeof secondsLeft === 'number' && secondsLeft <= 0 && !session.actor_directive));
  }, [isEnabled]);

  const handToActor = useCallback(async () => {
    if (!canHandToActor()) return;
    const generation = hostGeneration.current;
    const intent = intentRevision.current;
    const sessionId = latest.current.controller.session!.id;
    const pending = actorHandoff.current;
    if (pending && !pending.cancelled && pending.generation === generation
      && pending.intent === intent && pending.sessionId === sessionId) return pending.promise;
    const transfer = { generation, intent, sessionId, hostKey, phase: latest.current.phase,
      cancelled: false, promise: Promise.resolve() };
    actorHandoff.current = transfer;
    const ownsHost = () => generation === hostGeneration.current && isEnabled()
      && hostKey === `${latest.current.params.conversationScope ?? ''}:${latest.current.params.flowName}:${latest.current.params.flowRunId}:${latest.current.params.questionId}`;
    const ownsTransfer = () => !transfer.cancelled && ownsHost()
      && intent === intentRevision.current && latest.current.phase === transfer.phase && canHandToActor(sessionId);
    transfer.promise = (async () => {
      if (latest.current.params.integrated && latest.current.params.guide.converse) {
        const heard = await present('handoff');
        if (!heard || !ownsTransfer()) return;
      }
      if (!ownsTransfer()) return;
      releasedGuideHandoff.current = transfer;
      await latest.current.params.guide.release();
      if (!ownsHost()) return;
      if (!ownsTransfer()) {
        await restoreReleasedGuide(generation);
        return;
      }
      guideOwnsMicrophone.current = false;
      setPhase('practice');
      await actorRef.current?.connect();
    })();
    try { await transfer.promise; }
    finally {
      if (actorHandoff.current === transfer) actorHandoff.current = null;
      if (releasedGuideHandoff.current === transfer) releasedGuideHandoff.current = null;
    }
  }, [canHandToActor, hostKey, isEnabled, present, restoreReleasedGuide]);

  const interceptActorControl = useCallback(async (transcript: string) => {
    if (!isEnabled()) return true;
    const text = normalize(transcript);
    // Scope controls explicitly so ordinary scenario dialogue such as "end
    // practice" is still evaluated as the learner's roleplay response.
    const integratedControls = Boolean(latest.current.params.integrated && latest.current.params.guide.converse);
    const command = text.match(integratedControls
      ? /^(?:wise friend|wisefriend)\s+(end practice|pause practice|repeat(?: that)?|leave practice)$/
      : /^wise friend\s+(end practice|pause practice|repeat(?: that)?|leave practice)$/)?.[1];
    const addressedGuide = integratedControls && /^(?:wise friend|wisefriend)(?:\s|$)/.test(text);
    if (!command && !addressedGuide) return false;
    const current = latest.current;
    const session = current.controller.session;
    if (session && current.params.flowRunId !== null && current.params.questionId !== null) {
      // Preserve actual recognized control words before actor teardown clears
      // its live caption. This is local presentation, never scene dialogue,
      // an evaluator input, a saved answer or a completion/action request.
      const key = `${current.params.conversationScope ?? ''}:${current.params.flowName}:${current.params.flowRunId}:${current.params.questionId}`;
      const record: VoiceRoleplayControlTranscript = {
        id: `roleplay-control-${current.params.flowRunId}-${session.id}-${hostGeneration.current}-${controlTranscriptSequence.current++}`,
        text: transcript.trim(), timestamp: new Date().toISOString(),
        conversationScope: current.params.conversationScope ?? '', flowRunId: current.params.flowRunId,
        questionId: current.params.questionId, roleplaySessionId: session.id,
      };
      setControlTranscriptState((previous) => ({ hostKey: key,
        transcripts: [...(previous.hostKey === key ? previous.transcripts : []), record] }));
    }
    intentRevision.current += 1;
    const intent = intentRevision.current;
    feedbackPlayback.current = null;
    const generation = hostGeneration.current;
    const sessionId = latest.current.controller.session?.id;
    suspendedPhase.current = 'practice';
    await returnToGuide();
    if (generation !== hostGeneration.current || intent !== intentRevision.current || !isEnabled()
      || latest.current.controller.session?.id !== sessionId
      || !['active', 'closing'].includes(latest.current.controller.session?.status ?? '')
      || latest.current.controller.session?.ui_mode === 'crisis') return true;
    if (!command) {
      setPhase('control_clarification');
      await present('control_clarification');
    } else if (command === 'end practice' || command === 'leave practice') {
      setPhase('end_confirmation');
      await speak('Would you like to end this practice? Say yes to end it, or cancel to continue.');
    } else {
      setPhase('paused');
      const lastLine = latest.current.controller.latestPersonaTurn?.text;
      await speak(command.startsWith('repeat') && lastLine
        ? `${lastLine} Say resume practice when you are ready.`
        : 'Practice capture is paused. Say resume practice to continue, or end practice.');
    }
    return true;
  }, [isEnabled, present, returnToGuide, speak]);

  const actor = useRoleplayVoiceSession({
    flowName: params.flowName,
    session: enabled && !guideOwnsMicrophone.current ? controller.session : null,
    personaName: controller.scenario?.persona.name ?? 'the persona',
    autoConnect: false,
    onControlTranscript: interceptActorControl,
    onLearnerTranscript: (text, eventId, onProgress) => ownsActorHost()
      ? latest.current.controller.submitTurn(text, 'voice', eventId, undefined, onProgress) : Promise.resolve(null),
    onActorTurn: (payload) => ownsActorHost()
      ? latest.current.controller.persistActorTurn(payload) : Promise.resolve(null),
    onTerminalPlaybackComplete: () => { if (ownsActorHost()) latest.current.controller.releaseTerminalTransition(); },
    onRuntimeClaim: () => ownsActorHost() ? latest.current.controller.claimRuntime() : Promise.resolve(null),
    onRuntimeSuspend: () => ownsActorHost() ? latest.current.controller.suspendRuntime() : Promise.resolve(null),
    onRuntimeReady: (ready) => ownsActorHost() ? latest.current.controller.readyRuntime(ready) : Promise.resolve(null),
    onInputFinalizing: (eventId) => ownsActorHost() ? latest.current.controller.inputFinalizing(eventId) : Promise.resolve(null),
    onRuntimeSession: (snapshot) => { if (ownsActorHost()) latest.current.controller.applyRuntimeSession(snapshot); },
  });
  actorRef.current = actor;
  const actorState = actor.state;
  const setRuntimeWaiting = controller.setRuntimeWaiting;
  const releaseTerminalTransition = controller.releaseTerminalTransition;
  const createDebrief = controller.createDebrief;

  useEffect(() => {
    hostGeneration.current += 1;
    intentRevision.current += 1;
    delivered.current = '';
    feedbackPreparation.current = '';
    feedbackAutoHeld.current = false;
    feedbackPlayback.current = null;
    continued.current = '';
    busy.current = false;
    queuedGuideTranscript.current = '';
    queuedGuideClarification.current = false;
    actorRetryCount.current = 0;
    guideOwnsMicrophone.current = true;
    guideReturn.current = null;
    actorHandoff.current = null;
    releasedGuideHandoff.current = null;
    automaticActorHandoff.current = '';
    setControlTranscriptState({ hostKey, transcripts: [] });
    setReflection('');
    setError(null);
    setPhase(enabled ? 'prebrief' : 'inactive');
    return () => {
      hostGeneration.current += 1;
      intentRevision.current += 1;
      feedbackPlayback.current = null;
      queuedGuideTranscript.current = '';
      queuedGuideClarification.current = false;
      actorRef.current?.cancelAndDisconnect();
    };
  }, [enabled, hostKey]);

  useEffect(() => {
    const handoff = actorHandoff.current;
    if (handoff && (handoff.cancelled || handoff.generation !== hostGeneration.current
      || handoff.intent !== intentRevision.current || handoff.phase !== phase || !canHandToActor(handoff.sessionId))) {
      handoff.cancelled = true;
      actorHandoff.current = null;
      // New words or a new host have their own hook fences. The unscoped
      // interrupt may only cancel this same-host, unchanged-intent handoff.
      if (handoff.generation === hostGeneration.current && handoff.hostKey === hostKey
        && handoff.intent === intentRevision.current && guideOwnsMicrophone.current) {
        latest.current.params.guide.interrupt?.();
      }
    }
    if (!enabled || controller.loading || !controller.scenario) return;
    const session = controller.session;
    if (session?.status === 'crisis_paused') {
      if (phase === 'support') return;
      const generation = hostGeneration.current;
      // Crisis output belongs to the existing fixed safety narration service.
      // Do not reconnect a generative guide or ask it to rewrite that notice.
      actorRef.current?.cancelAndDisconnect();
      setPhase('support');
      guideOwnsMicrophone.current = true;
      void latest.current.params.guide.release().then(async () => {
        if (generation === hostGeneration.current && isEnabled()) await latest.current.params.onCrisis?.(session);
      }).catch((failure: Error) => setError(failure.message));
      return;
    }
    if (session?.status === 'safety_paused') {
      if (phase === 'clarify') return;
      const generation = hostGeneration.current;
      void returnToGuide().then(() => {
        if (generation !== hostGeneration.current || !isEnabled()) return;
        setPhase('clarify');
      }).catch((failure: Error) => setError(failure.message));
      return;
    }
    if (session?.status === 'evaluation_paused' || session?.actor_error) {
      if (phase === 'recovery') return;
      const generation = hostGeneration.current;
      void returnToGuide().then(() => {
        if (generation === hostGeneration.current && isEnabled()) setPhase('recovery');
      }).catch((failure: Error) => setError(failure.message));
      return;
    }
    // Retry preparation clears the old attempt before its opening request
    // settles. Its retained screen/phase cannot present that attempt again.
    if (controller.processing || !session) return;
    const guideOwnedTerminal = guideOwnsMicrophone.current
      && Boolean(session && ['completed', 'ended_early'].includes(session.status));
    if (guideOwnedTerminal && controller.terminalTransitionPending) {
      // The standalone controller waits for an actor's audible drain. A
      // resumed/guide-owned ending has no actor playback to release that UI
      // transition; the authoritative terminal result still owns the outcome.
      releaseTerminalTransition();
    }
    if (controller.screen === 'debrief' || guideOwnedTerminal) {
      if (['reflection', 'reflection_confirmation', 'feedback_loading', 'feedback_recovery', 'feedback'].includes(phase)) return;
      const generation = hostGeneration.current;
      const intent = intentRevision.current;
      const sessionId = session.id;
      const ownedTerminalSession = () => {
        const current = latest.current;
        const currentSession = current.controller.session;
        if (generation !== hostGeneration.current || intent !== intentRevision.current || !isEnabled()
          || current.controller.processing || currentSession?.id !== sessionId
          || !['completed', 'ended_early'].includes(currentSession.status)
          || currentSession.ui_mode === 'crisis' || currentSession.ui_mode === 'clarify') return null;
        return currentSession;
      };
      void returnToGuide().then(() => {
        const currentSession = ownedTerminalSession();
        if (!currentSession) return;
        const draft = latest.current.params.draft;
        if (hasFeedback(currentSession)) setPhase('feedback');
        else if (draft?.roleplay_session_id === currentSession.id && draft.text.trim()) {
          setReflection(draft.text);
          setPhase(integrated && draft.phase === 'reflection' ? 'reflection' : 'reflection_confirmation');
        } else setPhase(integrated && !roleplayWantsReflection(latest.current.params.voiceSettings) ? 'feedback_loading' : 'reflection');
      }).catch((failure: Error) => { if (ownedTerminalSession()) setError(failure.message); });
    } else if (session && phase === 'prebrief' && controller.screen === 'practice') {
      // Resume a persisted attempt only after releasing the current guide.
      // Once offered, timer updates cannot override a learner interruption.
      const key = `${hostGeneration.current}:${session.id}`;
      if (canHandToActor(session.id) && automaticActorHandoff.current !== key) {
        automaticActorHandoff.current = key;
        void handToActor().catch((failure: Error) => setError(failure.message));
      }
    }
  }, [canHandToActor, controller.loading, controller.processing, controller.scenario, controller.screen,
    controller.secondsLeft, controller.session, controller.terminalTransitionPending, releaseTerminalTransition,
    enabled, handToActor, hostKey, integrated, isEnabled, phase, returnToGuide]);

  useEffect(() => {
    if (!enabled || !integrated || phase !== 'feedback_loading' || !controller.session) return;
    const generation = hostGeneration.current;
    const sessionId = controller.session.id;
    const key = `${generation}:${sessionId}:${reflection}`;
    // Another request can own the canonical debrief claim. Wait for the
    // controller's existing polling instead of generating or reading a failure.
    if (controller.session.debrief_pending) {
      feedbackPreparation.current = `pending:${key}`;
      return;
    }
    if (controller.processing) return;
    if (hasFeedback(controller.session)) {
      setPhase('feedback');
      return;
    }
    if (feedbackPreparation.current === `pending:${key}`) {
      setPhase('feedback_recovery');
      return;
    }
    if (feedbackPreparation.current === key) return;
    feedbackPreparation.current = key;
    void createDebrief(reflection).then((result) => {
      if (generation !== hostGeneration.current || !isEnabled()
        || latest.current.controller.session?.id !== sessionId || latest.current.phase !== 'feedback_loading') return;
      if (result?.debrief_pending) {
        feedbackPreparation.current = `pending:${key}`;
        return;
      }
      setPhase(result && hasFeedback(result) ? 'feedback' : 'feedback_recovery');
    }).catch(() => {
      if (generation === hostGeneration.current && isEnabled()
        && latest.current.controller.session?.id === sessionId && latest.current.phase === 'feedback_loading') setPhase('feedback_recovery');
    });
  }, [controller.processing, controller.session, createDebrief, enabled, integrated, isEnabled, phase, reflection]);

  useEffect(() => {
    if (!integrated || !enabled || phase !== 'feedback') return;
    void continueAfterFeedback().catch((failure: Error) => setError(failure.message));
  }, [continueAfterFeedback, controller.session, enabled, integrated, phase]);

  useEffect(() => {
    if (!enabled || controller.loading || controller.processing || !controller.scenario) return;
    if (!controller.session && ['reflection', 'reflection_confirmation', 'feedback', 'feedback_recovery'].includes(phase)) return;
    if (integrated && ['prebrief', 'reflection', 'feedback', 'feedback_recovery'].includes(phase)) {
      if (phase === 'prebrief' && controller.session) return;
      if (phase === 'feedback' && feedbackAutoHeld.current) return;
      const key = `${hostKey}:${controller.session?.id ?? 'new'}:${phase}:${reflection}:${JSON.stringify(controller.session?.debrief ?? {})}`;
      if (delivered.current === key) return;
      delivered.current = key;
      const action = phase === 'feedback' ? deliverFeedback() : present(phase as VoiceRoleplayPresentation);
      void action.catch((failure: Error) => { delivered.current = ''; setError(failure.message); });
      return;
    }
    let text = '';
    if (phase === 'prebrief' && !controller.session) text = voiceRoleplayPrebrief(controller.scenario);
    if (phase === 'reflection') text = `What did you notice about ${controller.scenario.persona.name}? What would you repeat or change next time? Share your reflection, or say skip reflection.`;
    if (phase === 'reflection_confirmation') text = `Your reflection: ${reflection} Is that right? Say yes to confirm, or cancel to revise.`;
    if (phase === 'feedback' && controller.session) text = voiceRoleplayFeedback(controller.session);
    if (phase === 'clarify') text = `We paused for a moment. Were you talking about ${controller.scenario.persona.name}, or about yourself? Whatever the answer, it is okay. If this is about you, the practice will pause and show safety guidance. Say the persona, or myself.`;
    if (phase === 'recovery') text = controller.session?.status === 'evaluation_paused'
      ? 'Your response is saved, but the evaluator could not continue. Say retry evaluation to try again without repeating your response, or leave practice.'
      : 'The practice voice could not continue. Say retry response to try again from the saved practice, or leave practice.';
    const key = `${hostKey}:${controller.session?.id ?? 'new'}:${phase}:${text}`;
    if (!text || delivered.current === key) return;
    delivered.current = key;
    void speak(text).catch((failure: Error) => {
      delivered.current = '';
      setError(failure.message);
    });
  }, [controller.loading, controller.processing, controller.scenario, controller.session, deliverFeedback, enabled, hostKey, integrated, phase, present, reflection, speak]);

  useEffect(() => {
    if (!enabled || phase !== 'practice' || actor.state !== 'error'
      || !['active', 'closing'].includes(controller.session?.status ?? '')) return undefined;
    if (actorRetryCount.current >= 3) {
      void returnToGuide().then(() => { if (isEnabled()) setPhase('recovery'); }).catch((failure: Error) => setError(failure.message));
      return undefined;
    }
    actorRetryCount.current += 1;
    const generation = hostGeneration.current;
    const timer = window.setTimeout(() => {
      if (generation === hostGeneration.current && isEnabled()
        && ['active', 'closing'].includes(latest.current.controller.session?.status ?? '')) void actorRef.current?.connect();
    }, 1000 * 2 ** (actorRetryCount.current - 1));
    return () => window.clearTimeout(timer);
  }, [actor.state, controller.session?.status, enabled, isEnabled, phase, returnToGuide]);

  useEffect(() => {
    setRuntimeWaiting(['connecting', 'finalizing', 'thinking', 'responding', 'speaking'].includes(actor.activity));
    return () => setRuntimeWaiting(false);
  }, [actor.activity, setRuntimeWaiting]);

  const handleGuideTranscript = useCallback(async (transcript: string) => {
    const current = latest.current;
    if (current.params.questionId === null || current.params.flowRunId === null) return false;
    if (current.controller.session?.status === 'crisis_paused') return true;
    const text = normalizeGuideIntent(transcript, integrated);
    const addressed = integrated && text !== normalize(transcript);
    if (!text && !addressed) return true;
    intentRevision.current += 1;
    feedbackPlayback.current = null;
    if (integrated && ['feedback_loading', 'feedback'].includes(current.phase)) feedbackAutoHeld.current = true;
    // Capture/settings commands retain the guide's canonical command path.
    if (isGuideControl(text)) {
      if (!addressed || !current.params.guide.command) return false;
      const command = text.startsWith('pause') ? 'pause_capture'
        : text.startsWith('resume') ? 'resume_capture' : 'settings';
      const generation = hostGeneration.current;
      const intent = intentRevision.current;
      const ownsControl = () => generation === hostGeneration.current && intent === intentRevision.current && isEnabled();
      try {
        const applied = await current.params.guide.command(command);
        if (ownsControl()) setError(applied ? null : 'The voice control could not be applied. Please try again.');
      } catch (failure) {
        if (ownsControl()) setError(failure instanceof Error ? failure.message : 'The voice control could not be applied. Please try again.');
      }
      return true;
    }
    if (busy.current) {
      if (integrated) {
        const explicit = isNo(text) || isGuideHelp(text) || isPracticeReadiness(text, true)
          || /^(?:leave(?: practice)?|exit(?: practice)?|pause(?: this)? practice|resume(?: practice)?|repeat(?: that)?|read that again|practice again|continue(?: module| course)?|end practice|stop practice)$/.test(text);
        queuedGuideTranscript.current = explicit ? transcript : '';
        queuedGuideClarification.current = !explicit;
      }
      return true;
    }
    if (!integrated && isGuideHelp(text) && GUIDE_HELP_PHASES.includes(current.phase)) return false;
    busy.current = true;
    const generation = hostGeneration.current;
    const isCurrent = () => generation === hostGeneration.current && isEnabled();
    setError(null);
    try {
      if (!text && addressed) {
        await present(current.phase === 'control_clarification' ? 'control_clarification' : 'meaning');
        return true;
      }
      if (/^(leave|leave practice|exit|exit practice)$/.test(text)) {
        await actorRef.current?.disconnectAsync();
        await current.params.onLeave();
        return true;
      }
      if (integrated && (isNo(text) || (current.phase === 'reflection' && isYes(text)))
        && !['end_confirmation', 'reflection_confirmation'].includes(current.phase)) {
        await present('meaning');
        return true;
      }
      if (isDefer(text)) {
        if (current.phase === 'prebrief') return false;
        await speak('End or complete the practice before deferring it. Say leave practice to exit, or continue the current activity.');
        return true;
      }
      if (/^(pause practice|pause this practice)$/.test(text)) {
        if (integrated && current.phase === 'control_clarification') {
          setPhase('paused');
          await present('paused');
        } else if (current.phase === 'paused') await speak('Practice is paused. Say resume practice when you are ready.');
        else if (current.phase === 'prebrief') await speak('The practice has not started. Say pause capture to pause the guide, or begin practice when you are ready.');
        else await speak('Say pause capture to pause the guide. Say resume capture when you are ready.');
        return true;
      }
      if (isGuideHelp(text) || /^(repeat|repeat that|read that again)$/.test(text)) {
        if (integrated && current.phase === 'control_clarification') {
          if (/^(repeat|repeat that|read that again)$/.test(text)) {
            setPhase('paused');
            const line = current.controller.latestPersonaTurn?.text;
            await speak(`${line ?? ''} Say resume practice when you are ready, or end practice.`);
          } else await present('control_clarification');
          return true;
        }
        if (integrated && current.phase === 'feedback_loading') {
          // A repeat while generation is pending requests its eventual actual
          // result; it never reads a placeholder or starts another generation.
          if (/^(repeat|repeat that|read that again)$/.test(text)) feedbackAutoHeld.current = false;
          return true;
        }
        if (integrated && ['prebrief', 'reflection', 'feedback', 'feedback_recovery'].includes(current.phase)) {
          if (current.phase === 'feedback') await deliverFeedback(/explain|more|detail/.test(text));
          else await present(current.phase as VoiceRoleplayPresentation, undefined, isGuideHelp(text) ? transcript : undefined);
          return true;
        }
        delivered.current = '';
        if (current.phase === 'prebrief' && current.controller.scenario) await speak(voiceRoleplayPrebrief(current.controller.scenario));
        else if (current.phase === 'feedback' && current.controller.session) await speak(voiceRoleplayFeedback(current.controller.session));
        else if (current.phase === 'paused') await speak(`${current.controller.latestPersonaTurn?.text ?? ''} Say resume practice when you are ready, or end practice.`);
        else if (current.phase === 'end_confirmation') await speak('Would you like to end this practice? Say yes to end it, or cancel to continue.');
        else if (current.phase === 'reflection_confirmation') await speak(`Your reflection: ${reflection} Is that right? Say yes to confirm, or cancel to revise.`);
        else if (current.phase === 'clarify') await speak('Were you talking about the persona, or about yourself? Say the persona, or myself.');
        else if (current.phase === 'recovery') await speak('Say retry evaluation, retry response, or leave practice.');
        else await speak('Share your reflection, or say skip reflection.');
        return true;
      }
      if (current.phase === 'support') return true;
      if (integrated && current.phase === 'feedback_loading') {
        // The actual server request owns this phase. Controls above may leave;
        // ordinary speech never fabricates feedback or starts another request.
        return true;
      }
      if (integrated && current.phase === 'feedback_recovery') {
        if (/^(?:retry|try again|retry feedback|try the feedback again|show(?: my)? feedback)$/.test(text)) {
          feedbackPreparation.current = '';
          feedbackAutoHeld.current = false;
          setPhase('feedback_loading');
        } else await present('feedback_recovery');
        return true;
      }
      if (current.phase === 'recovery') {
        if (/^(retry|try again|retry evaluation|retry response|reconnect)$/.test(text)) {
          actorRetryCount.current = 0;
          if (current.controller.session?.status === 'evaluation_paused') {
            const result = await current.controller.retryEvaluation();
            if (!isCurrent()) return true;
            if (result?.status === 'active' || result?.status === 'closing') await handToActor();
            else if (integrated && result && ['completed', 'ended_early'].includes(result.status)) {
              current.controller.releaseTerminalTransition();
              setPhase(hasFeedback(result) ? 'feedback'
                : roleplayWantsReflection(current.params.voiceSettings) ? 'reflection' : 'feedback_loading');
            }
            else await speak('The evaluator is still unavailable. Your response remains saved. Say retry evaluation to try again.');
          } else if (current.controller.session?.actor_error) {
            // The existing retry endpoint regenerates and persists the actor's
            // actual text. Narrate that result before offering spoken resume;
            // the next actor connection restores voice output through its
            // existing Realtime configuration endpoint.
            const result = await current.controller.retryActor();
            if (!isCurrent()) return true;
            if (result && !result.actor_error) {
              const line = result.turns.filter((turn) => turn.speaker === 'persona' && !turn.metadata?.safety_only).at(-1)?.text;
              await speak(`${line ?? 'The response is ready.'} Say resume practice when you are ready.`);
              if (['completed', 'ended_early'].includes(result.status)) {
                current.controller.releaseTerminalTransition();
                setPhase(integrated && !roleplayWantsReflection(current.params.voiceSettings) ? 'feedback_loading' : 'reflection');
              } else setPhase('paused');
            } else await speak('The response is still unavailable. Say retry response to try again.');
          } else await handToActor();
        } else await speak('Say retry evaluation, retry response, or leave practice.');
        return true;
      }
      if (current.phase === 'clarify') {
        if (/^(the persona|persona|about the persona|about (?:the )?character)$/.test(text)
          || text === normalize(current.controller.scenario?.persona.name ?? '')) {
          const resolved = await current.controller.resolveSafety('persona');
          if (!isCurrent()) return true;
          if (resolved?.status === 'active') await handToActor();
        } else if (/^(myself|me|about me|about myself|self)$/.test(text)) {
          await current.controller.resolveSafety('self');
        } else await speak('Were you talking about the persona, or about yourself?');
        return true;
      }
      if (current.phase === 'end_confirmation') {
        if (isYes(text)) {
          const result = await current.controller.endPractice();
          if (!isCurrent()) return true;
          if (result && ['completed', 'ended_early'].includes(result.status)) {
            current.controller.releaseTerminalTransition();
            setPhase(integrated && !roleplayWantsReflection(current.params.voiceSettings) ? 'feedback_loading' : 'reflection');
          } else await speak('The practice could not end. Say yes to retry, or cancel to continue.');
        } else if (isNo(text)) {
          setPhase(suspendedPhase.current);
          await handToActor();
        } else await speak('Say yes to end the practice, or cancel to continue.');
        return true;
      }
      if (/^(end practice|stop practice)$/.test(text)
        || (integrated && current.phase === 'control_clarification' && /^(end|stop)$/.test(text))) {
        if (!current.controller.session || current.phase === 'prebrief') {
          await speak('The practice has not started. Say begin practice when you are ready, or leave practice.');
          return true;
        }
        suspendedPhase.current = current.phase;
        setPhase('end_confirmation');
        await speak('Would you like to end this practice? Say yes to end it, or cancel to continue.');
        return true;
      }
      if (current.phase === 'prebrief' && (isPracticeReadiness(text, integrated)
        || (integrated && current.controller.session && /^(?:resume practice|continue practice)$/.test(text)))) {
        if (integrated && current.controller.session) {
          if (['active', 'closing'].includes(current.controller.session.status)) await handToActor();
          return true;
        }
        const intent = intentRevision.current;
        if (integrated) {
          const heard = await present('handoff');
          if (!heard || intent !== intentRevision.current || !isCurrent()) return true;
        }
        await current.params.guide.release();
        if (!isCurrent()) return true;
        if (integrated && intent !== intentRevision.current) {
          await restoreReleasedGuide(generation);
          return true;
        }
        const started = await current.controller.startPractice('voice');
        if (!isCurrent()) return true;
        if (integrated && intent !== intentRevision.current) {
          await restoreReleasedGuide(generation);
          setPhase('paused');
          return true;
        }
        if (!started) {
          await current.params.guide.reconnect();
          await speak('The practice could not start. Say begin practice to try again.');
        } else {
          guideOwnsMicrophone.current = false;
          setPhase('practice');
        }
        return true;
      }
      if ((current.phase === 'paused' || (integrated && current.phase === 'control_clarification'))
        && /^(resume|resume practice|continue practice)$/.test(text)) {
        await handToActor();
        return true;
      }
      if (['reflection', 'reflection_confirmation', 'feedback'].includes(current.phase)
        && /^(practice again|retry|try again)$/.test(text)) {
        await current.controller.prepareRetry();
        if (!isCurrent()) return true;
        feedbackAutoHeld.current = false;
        setPhase('prebrief');
        return true;
      }
      if (current.phase === 'reflection' && /^(skip reflection|skip the reflection|show feedback|show my feedback)$/.test(text)) {
        if (integrated) {
          setReflection('');
          feedbackPreparation.current = '';
          feedbackAutoHeld.current = false;
          setPhase('feedback_loading');
          return true;
        }
        const result = await current.controller.createDebrief('');
        if (!isCurrent()) return true;
        if (result && hasFeedback(result)) setPhase('feedback');
        else await speak('Feedback could not be prepared. Say show feedback to try again.');
        return true;
      }
      if (current.phase === 'reflection_confirmation') {
        if (isYes(text)) {
          if (integrated) {
            feedbackPreparation.current = '';
            feedbackAutoHeld.current = false;
            setPhase('feedback_loading');
            return true;
          }
          const result = await current.controller.createDebrief(reflection);
          if (!isCurrent()) return true;
          if (result && hasFeedback(result)) setPhase('feedback');
          else await speak('Feedback could not be prepared. Say yes to try again, or cancel to revise your reflection.');
        } else if (isNo(text)) {
          setPhase('reflection');
        } else {
          setReflection(transcript.trim());
        }
        return true;
      }
      if (current.phase === 'reflection') {
        if (addressed && isPracticeReadinessOrResume(text, integrated)) {
          await present('meaning');
          return true;
        }
        setReflection(transcript.trim());
        feedbackAutoHeld.current = false;
        setPhase(integrated ? 'feedback_loading' : 'reflection_confirmation');
        return true;
      }
      if (integrated && current.phase === 'feedback'
        && /^(?:tell me more|more(?: details?)?|more feedback|explain(?: the feedback)?|explain that|what (?:worked|could i improve))$/.test(text)) {
        await deliverFeedback(true);
        return true;
      }
      if (current.phase === 'feedback' && /^(continue|continue module|continue course|finish|done)$/.test(text)) {
        const session = current.controller.session;
        if (roleplayHasConfirmedCompletion(session)) {
          if (integrated) await deliverFeedback();
          else await current.params.onContinue(session!);
        } else await speak('This attempt has not met the practice goal. Say practice again, or leave practice.');
        return true;
      }
      if (current.phase === 'prebrief') {
        if (integrated) await present('ready');
        else await speak('Say begin practice when you are ready, or repeat to hear the situation again.');
      } else if (current.phase === 'paused') {
        if (integrated) await present('paused');
        else await speak('Say resume practice, or end practice.');
      } else if (integrated && current.phase === 'control_clarification') {
        await present('control_clarification');
      }
      return true;
    } catch (failure) {
      if (!isCurrent()) return true;
      const message = failure instanceof Error ? failure.message : 'The practice could not continue. Please try again.';
      setError(message);
      if (!integrated && guideOwnsMicrophone.current) await speak(message);
      return true;
    } finally {
      if (isCurrent()) {
        busy.current = false;
        const queued = queuedGuideTranscript.current;
        const clarifyQueued = queuedGuideClarification.current;
        queuedGuideTranscript.current = '';
        queuedGuideClarification.current = false;
        if (integrated && queued) void guideTranscriptHandler.current(queued);
        else if (integrated && clarifyQueued && guideOwnsMicrophone.current) void present('meaning').catch(() => undefined);
        else if (integrated) void continueAfterFeedback().catch((failure: Error) => setError(failure.message));
      }
    }
  }, [continueAfterFeedback, deliverFeedback, handToActor, integrated, isEnabled, present, reflection, restoreReleasedGuide, speak]);
  guideTranscriptHandler.current = handleGuideTranscript;

  useEffect(() => {
    if (!enabled || guideOwnsMicrophone.current || phase !== 'practice'
      || !['active', 'closing'].includes(controller.session?.status ?? '')) return;
    if (actorState === 'idle') void actorRef.current?.connect();
  }, [actorState, controller.session, enabled, phase]);

  const transcriptMetadata = useCallback((transcript: string): Record<string, unknown> => {
    const current = latest.current;
    const session = current.controller.session;
    const metadata = {
      roleplay_host: true,
      ...(session ? { roleplay_session_id: session.id } : {}),
      ...(GUIDE_HELP_PHASES.includes(current.phase) ? { roleplay_guide_phase: current.phase } : {}),
    };
    if (!session) return metadata;
    const text = normalizeGuideIntent(transcript, integrated);
    const addressed = integrated && text !== normalize(transcript);
    const command = /^(?:repeat(?: that)?|read that again|settings|voice settings|change voice|read settings|leave(?: practice)?|exit(?: practice)?|practice again|retry|try again|skip(?: the)? reflection|show(?: my)? feedback|end practice|stop practice|continue(?: module| course)?|finish|done)$/;
    let draft: VoiceRoleplayDraft | undefined;
    if (isGuideHelp(text) || isGuideControl(text) || isDefer(text) || /^(pause practice|pause this practice)$/.test(text)
      || (addressed && isPracticeReadinessOrResume(text, integrated))) {
      return metadata;
    }
    if (current.phase === 'reflection' && text && !command.test(text) && !(integrated && (isNo(text) || isYes(text)))) {
      draft = { phase: integrated ? 'reflection' : 'reflection_confirmation', text: transcript.trim(), roleplay_session_id: session.id };
    } else if (current.phase === 'reflection_confirmation') {
      draft = {
        phase: isNo(text) ? 'reflection' : 'reflection_confirmation',
        text: isNo(text) ? '' : isYes(text) || command.test(text) ? reflection : transcript.trim(),
        roleplay_session_id: session.id,
      };
    }
    return { ...metadata, ...(draft ? { roleplay_host_draft: draft } : {}) };
  }, [integrated, reflection]);

  const releaseActor = useCallback(async () => {
    intentRevision.current += 1;
    feedbackPlayback.current = null;
    queuedGuideTranscript.current = '';
    queuedGuideClarification.current = false;
    await actorRef.current?.disconnectAsync({ flushPendingLearner: true });
    guideOwnsMicrophone.current = true;
  }, []);

  return {
    active: enabled, phase, handleGuideTranscript, transcriptMetadata, controller,
    controlTranscripts: controlTranscriptState.hostKey === hostKey ? controlTranscriptState.transcripts : [],
    actorState: actor.state, actorActivity: actor.activity,
    liveTranscript: actor.liveTranscript ?? '', liveActorTranscript: actor.liveActorTranscript ?? '',
    liveActorGeneration: actor.latency?.generation ?? null,
    releaseActor,
    error: error ?? actor.error,
  };
};
